import { Fragment, useMemo, useState } from 'react'
import {
  Alert,
  BarChart,
  Card,
  DonutChart,
  LineChart,
  Spinner,
  StatCard,
  Table,
  formatCurrency,
  formatDate,
  formatNumber,
  type BarDatum,
  type Series,
  type TableColumn,
  CHART_COLORS,
} from '@/ui'
import { BUCKET_LABELS, CHANNEL_LABELS, PRODUCT_TYPE_LABELS, WEEKDAY_LABELS } from './labels'
import type { PaymentAttributionData } from './types'
import { HeatmapCellDrawer } from './HeatmapCellDrawer'
import styles from '../PaymentAttribution.module.css'

const pad = (n: number) => String(n).padStart(2, '0')

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 시간대별 sparse rows → 기간 전체 시간 축으로 채움 (빈 시간=0) */
function fillHourly(
  rows: PaymentAttributionData['hourly'],
  from: string,
  to: string,
): { date: string; hour: number; payments: number; netRevenue: number; clicks: number }[] {
  const byKey = new Map(rows.map((r) => [`${r.date}|${r.hour}`, r]))
  const out: { date: string; hour: number; payments: number; netRevenue: number; clicks: number }[] = []
  const cursor = new Date(from)
  cursor.setMinutes(0, 0, 0)
  const end = new Date(to)
  while (cursor <= end) {
    const date = toYmd(cursor)
    const hour = cursor.getHours()
    const found = byKey.get(`${date}|${hour}`)
    out.push(found ?? { date, hour, payments: 0, netRevenue: 0, clicks: 0 })
    cursor.setHours(cursor.getHours() + 1)
  }
  return out
}

// ── 요일×시간 히트맵 (GitHub 컨트리뷰션 스타일 — 이산 4단계) ──
/** 값 → 색 단계 0~4. 0=없음, 1~4는 최댓값 대비 사분위. */
function heatLevel(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0
  const ratio = value / max
  if (ratio <= 0.25) return 1
  if (ratio <= 0.5) return 2
  if (ratio <= 0.75) return 3
  return 4
}

function WeekdayHourHeatmap({
  matrix,
  max,
  onCellClick,
}: {
  matrix: number[][]
  max: number
  /** 결제가 있는 셀 클릭 시 (요일 dow, 시간 hour) 전달 */
  onCellClick?: (dow: number, hour: number) => void
}) {
  const hours = Array.from({ length: 24 }, (_, h) => h)
  return (
    <div className={styles.heatmapWrap}>
      <div className={styles.heatmapGrid}>
        <div className={styles.heatmapCorner} />
        {hours.map((h) => (
          <div key={`h-${h}`} className={styles.heatmapColLabel}>{h % 3 === 0 ? h : ''}</div>
        ))}
        {WEEKDAY_LABELS.map((wd, dow) => (
          <Fragment key={wd}>
            <div className={styles.heatmapRowLabel}>{wd}</div>
            {hours.map((h) => {
              const v = matrix[dow][h]
              const level = heatLevel(v, max)
              const clickable = v > 0 && onCellClick != null
              return (
                <div
                  key={`${dow}-${h}`}
                  className={`${styles.heatmapCell} ${clickable ? styles.heatmapCellClickable : ''}`}
                  data-level={level}
                  title={
                    v > 0
                      ? `${wd}요일 ${h}시 — 결제 ${v}건 (클릭해 상세 보기)`
                      : `${wd}요일 ${h}시 — 결제 없음`
                  }
                  onClick={clickable ? () => onCellClick(dow, h) : undefined}
                />
              )
            })}
          </Fragment>
        ))}
      </div>
      {/* 범례 — 적음 → 많음 */}
      <div className={styles.heatmapLegend}>
        <span>적음</span>
        {[0, 1, 2, 3, 4].map((lv) => (
          <span key={lv} className={styles.heatmapCell} data-level={lv} />
        ))}
        <span>많음</span>
      </div>
    </div>
  )
}

interface OverviewTabProps {
  data: PaymentAttributionData | null
  error: string | null
  /** 총 광고비(실결제 ROAS 계산용). 셸에서 ads 리포트로 조회해 주입. 미로딩=null */
  adSpend: number | null
}

/** ROAS(%) 표기 — 순매출/광고비. 광고비 0 또는 미로딩이면 '—'. */
function roasPct(revenue: number, spend: number | null): string {
  if (spend == null || spend <= 0) return '—'
  return `${((revenue / spend) * 100).toFixed(0)}%`
}

export function OverviewTab({ data, error, adSpend }: OverviewTabProps) {
  // ── 차트 데이터 가공 ──
  const hourlyFilled = useMemo(
    () => (data ? fillHourly(data.hourly, data.from, data.to) : []),
    [data],
  )
  const toIso = (r: { date: string; hour: number }) =>
    `${r.date}T${String(r.hour).padStart(2, '0')}:00:00`

  const clickSeries: Series[] = [{
    key: 'clicks',
    label: '링크 클릭',
    color: CHART_COLORS.info,
    data: hourlyFilled.map((r) => ({ date: toIso(r), value: r.clicks })),
  }]
  const paymentSeries: Series[] = [{
    key: 'payments',
    label: '결제 건수',
    color: CHART_COLORS.success,
    data: hourlyFilled.map((r) => ({ date: toIso(r), value: r.payments })),
  }]

  const sourceBars: BarDatum[] = (data?.bySource ?? []).slice(0, 8).map((s) => ({
    label: s.utmContent ? `${s.source}/${s.utmContent}` : s.source,
    value: s.netRevenue,
    color:
      s.source === '(유입미상)' ? CHART_COLORS.gray
      : s.source === '(direct)' ? CHART_COLORS.warning
      : CHART_COLORS.primary,
  }))

  const bucketBars: BarDatum[] = (data?.signupBuckets ?? []).map((b) => ({
    label: BUCKET_LABELS[b.bucket] ?? b.bucket,
    value: b.payments,
    color: b.bucket.startsWith('0') ? CHART_COLORS.gray : CHART_COLORS.primary,
  }))

  const channelDonut = useMemo(() => {
    if (!data) return []
    const byChannel = new Map<string, number>()
    for (const r of data.byChannelType) {
      byChannel.set(r.channel, (byChannel.get(r.channel) ?? 0) + r.netRevenue)
    }
    const colors: Record<string, string> = { SITE: CHART_COLORS.primary, SMARTSTORE: CHART_COLORS.success }
    return [...byChannel.entries()].map(([channel, value]) => ({
      label: CHANNEL_LABELS[channel] ?? channel,
      value,
      color: colors[channel] ?? CHART_COLORS.gray,
    }))
  }, [data])

  // UTM 캠페인별 매출 (rows 재집계)
  const campaignBars: BarDatum[] = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of data?.rows ?? []) {
      const key = r.utmCampaign || '(none)'
      m.set(key, (m.get(key) ?? 0) + r.netAmount)
    }
    return [...m.entries()]
      .map(([label, value]) => ({
        label,
        value,
        color: label === '(none)' ? CHART_COLORS.gray : CHART_COLORS.primary,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [data])

  // 상품 유형별 매출/건수 (byChannelType 재집계)
  const typeRows = useMemo(() => {
    const m = new Map<string, { payments: number; netRevenue: number }>()
    for (const r of data?.byChannelType ?? []) {
      const e = m.get(r.type) ?? { payments: 0, netRevenue: 0 }
      e.payments += r.payments
      e.netRevenue += r.netRevenue
      m.set(r.type, e)
    }
    return [...m.entries()]
      .map(([type, v]) => ({ type, ...v }))
      .sort((a, b) => b.netRevenue - a.netRevenue)
  }, [data])

  // 유입 도메인(referrer)별 순매출
  const referrerBars: BarDatum[] = (data?.byReferrer ?? []).slice(0, 8).map((r) => ({
    label: r.domain,
    value: r.netRevenue,
    color: r.domain.startsWith('(') ? CHART_COLORS.gray : CHART_COLORS.primary,
  }))

  // 요일×시간 히트맵 (결제 건수). rows 의 서버 산출 dow/hour(KST)로 집계해
  // 드릴다운 드로어의 필터 결과와 정확히 일치시킨다(브라우저 TZ 영향 없음).
  const heatmap = useMemo(() => {
    const matrix: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0))
    let max = 0
    for (const r of data?.rows ?? []) {
      matrix[r.dow][r.hour] += 1
      if (matrix[r.dow][r.hour] > max) max = matrix[r.dow][r.hour]
    }
    return { matrix, max }
  }, [data])

  // 히트맵 셀 드릴다운 드로어 상태
  const [selectedCell, setSelectedCell] = useState<{ dow: number; hour: number } | null>(null)

  const typeColumns: TableColumn<{ type: string; payments: number; netRevenue: number }>[] = [
    { key: 'type', label: '상품', render: (r) => PRODUCT_TYPE_LABELS[r.type] ?? r.type },
    { key: 'payments', label: '건수', render: (r) => `${formatNumber(r.payments)}건`, align: 'right' as const, width: '80px' },
    { key: 'netRevenue', label: '순매출', render: (r) => formatCurrency(r.netRevenue), align: 'right' as const, width: '110px' },
  ]

  const netRevenue = data?.summary.netRevenue ?? 0

  return (
    <>
      {/* KPI — 광고 효율(ROAS) 중심. ROAS = 순매출 / 총 광고비(실결제 기준). */}
      <div className={styles.statGrid}>
        <StatCard label="순매출" value={formatCurrency(netRevenue)} />
        <StatCard label="총 광고비" value={adSpend != null ? formatCurrency(adSpend) : '—'} />
        <StatCard label="ROAS(실결제)" value={data ? roasPct(netRevenue, adSpend) : '—'} />
        <StatCard label="결제 건수" value={`${formatNumber(data?.summary.payments ?? 0)}건`} />
      </div>

      {/* 요일×시간 결제 히트맵 — 광고 예산·발송 시간대 배분 근거 (상단 승격) */}
      <Card className={styles.heatmapCard}>
        <p className={styles.cardTitle}>요일 × 시간 결제 히트맵</p>
        {!data ? (
          <div className={styles.center}><Spinner /></div>
        ) : (
          <WeekdayHourHeatmap
            matrix={heatmap.matrix}
            max={heatmap.max}
            onCellClick={(dow, hour) => setSelectedCell({ dow, hour })}
          />
        )}
        <p className={styles.cardCaption}>결제 건수 기준. 셀을 클릭하면 해당 시간대 결제 상세를 볼 수 있습니다. 광고 예산·발송 시간대 배분 근거로 활용합니다.</p>
      </Card>

      {/* 핵심 추이/구성 — 결제 추이 + 채널별 순매출 */}
      <div className={styles.chartGrid}>
        <Card>
          <p className={styles.cardTitle}>결제 건수 (시간대별)</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : (
              <LineChart
                series={paymentSeries}
                showTooltip
                formatTooltip={(p) => `${formatDate(p.date, 'M.d HH시')} — 결제 ${formatNumber(p.value)}건`}
              />
            )}
          </div>
          <p className={styles.cardCaption}>스마트스토어 결제 시각은 cron 등록 시각(≤5분 오차)입니다.</p>
        </Card>
        <Card>
          <p className={styles.cardTitle}>채널별 순매출</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : channelDonut.length > 0 ? (
              <DonutChart
                data={channelDonut}
                showTooltip
                showLegend
                formatTooltip={(d, pct) => `${d.label}: ${formatCurrency(d.value)} (${(pct * 100).toFixed(0)}%)`}
              />
            ) : (
              <Alert variant="info">해당 기간 결제가 없습니다.</Alert>
            )}
          </div>
        </Card>
      </div>

      {/* ── 상세 분석 ── (가입 시점·상품유형·유입귀속 등 보조 지표) */}
      <h2 className={styles.sectionTitle}>상세 분석</h2>

      {/* 유입미상 규모 — KPI에서 상세로 강등 */}
      <div className={styles.statGrid}>
        <StatCard
          label="유입미상 건수"
          value={`${formatNumber(data?.summary.unattributedPayments ?? 0)}건`}
          change={
            data && data.summary.payments > 0
              ? `${((data.summary.unattributedPayments / data.summary.payments) * 100).toFixed(0)}%`
              : undefined
          }
        />
        <StatCard label="유입미상 매출" value={formatCurrency(data?.summary.unattributedRevenue ?? 0)} />
      </div>

      <div className={styles.chartGrid}>
        <Card>
          <p className={styles.cardTitle}>결제자 가입 시점 (지연 전환)</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : bucketBars.length > 0 ? (
              <BarChart
                data={bucketBars}
                direction="horizontal"
                showTooltip
                formatTooltip={(d) => `${d.label}: ${formatNumber(d.value)}건`}
              />
            ) : (
              <Alert variant="info">해당 기간 결제가 없습니다.</Alert>
            )}
          </div>
          <p className={styles.cardCaption}>"기간 내 가입"이 크면 광고 즉시 전환, "이전 가입"이 크면 과거 유입의 지연 구매입니다.</p>
        </Card>
        <Card>
          <p className={styles.cardTitle}>상품 유형별 매출/건수</p>
          <Table<{ type: string; payments: number; netRevenue: number }>
            columns={typeColumns}
            data={typeRows}
            size="sm"
            rowKey={(r) => r.type}
            isLoading={!data && !error}
            emptyMessage="해당 기간 결제가 없습니다"
          />
        </Card>
      </div>

      <div className={styles.chartGrid}>
        <Card>
          <p className={styles.cardTitle}>유입 소스별 순매출 (first-touch)</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : sourceBars.length > 0 ? (
              <BarChart
                data={sourceBars}
                direction="horizontal"
                showTooltip
                formatTooltip={(d) => `${d.label}: ${formatCurrency(d.value)}`}
              />
            ) : (
              <Alert variant="info">해당 기간 결제가 없습니다.</Alert>
            )}
          </div>
          <p className={styles.cardCaption}>
            회색=유입미상(미활성화 스토어 주문), 주황=direct. UTM은 자사 사이트 첫 방문 기준이라 하한선입니다.
          </p>
        </Card>
        <Card>
          <p className={styles.cardTitle}>UTM 캠페인별 순매출</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : campaignBars.length > 0 ? (
              <BarChart
                data={campaignBars}
                direction="horizontal"
                showTooltip
                formatTooltip={(d) => `${d.label}: ${formatCurrency(d.value)}`}
              />
            ) : (
              <Alert variant="info">해당 기간 결제가 없습니다.</Alert>
            )}
          </div>
          <p className={styles.cardCaption}>회색=(none). 자사 사이트 first-touch 캠페인 기준이라 스토어 직행은 잡히지 않습니다.</p>
        </Card>
        <Card>
          <p className={styles.cardTitle}>유입 도메인별 순매출 (referrer)</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : referrerBars.length > 0 ? (
              <BarChart
                data={referrerBars}
                direction="horizontal"
                showTooltip
                formatTooltip={(d) => `${d.label}: ${formatCurrency(d.value)}`}
              />
            ) : (
              <Alert variant="info">해당 기간 결제가 없습니다.</Alert>
            )}
          </div>
          <p className={styles.cardCaption}>가입 시 저장된 첫 방문 referrer의 호스트명. 회색=direct/유입미상.</p>
        </Card>
        <Card>
          <p className={styles.cardTitle}>마케팅 링크 클릭 (시간대별)</p>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : (
              <LineChart
                series={clickSeries}
                showTooltip
                formatTooltip={(p) => `${formatDate(p.date, 'M.d HH시')} — 클릭 ${formatNumber(p.value)}`}
              />
            )}
          </div>
          <p className={styles.cardCaption}>/go 단축링크 클릭(봇 제외). 결제 추이와 시간대를 대조해 광고 효과를 가늠합니다.</p>
        </Card>
      </div>

      <HeatmapCellDrawer
        cell={selectedCell}
        rows={data?.rows ?? []}
        onClose={() => setSelectedCell(null)}
      />
    </>
  )
}
